import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/ui-kit/PageBits';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Search, GraduationCap, Clock, Calendar, User as UserIcon, Circle, CheckCircle2, Users2, X, UserMinus, UserPlus, ArrowRightLeft } from 'lucide-react';

const DAYS = [
  { key: 'monday',    label: 'Lun' },
  { key: 'tuesday',   label: 'Mar' },
  { key: 'wednesday', label: 'Mié' },
  { key: 'thursday',  label: 'Jue' },
  { key: 'friday',    label: 'Vie' },
  { key: 'saturday',  label: 'Sáb' },
  { key: 'sunday',    label: 'Dom' },
];
const DAY_ES = Object.fromEntries(DAYS.map(d => [d.key, { short: d.label, full: {
  monday:'Lunes',tuesday:'Martes',wednesday:'Miércoles',thursday:'Jueves',friday:'Viernes',saturday:'Sábado',sunday:'Domingo'
}[d.key] }]));

const moduleTier = (n) => {
  if (!n) return null;
  if (n <= 4) return { label: 'Básico',      badge: 'bg-emerald-100 text-emerald-700' };
  if (n <= 8) return { label: 'Intermedio',  badge: 'bg-amber-100 text-amber-700' };
  return          { label: 'Avanzado',    badge: 'bg-rose-100 text-rose-700' };
};

const emptyForm = {
  group_code: '', name: '', module_slot: '', teacher_id: '',
  schedule_days: [], start_time: '19:00', end_time: '20:00',
  active: true, description: '',
};

export default function AdminGroups() {
  const [groups, setGroups] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [q, setQ] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  // Fase B-5B: gestión de alumnos por grupo
  const [membersByGroup, setMembersByGroup] = useState({}); // gid → students[]
  const [membersOpen, setMembersOpen] = useState(false);
  const [membersGroup, setMembersGroup] = useState(null); // group actual del drawer
  const [addOpen, setAddOpen] = useState(false);
  const [addQ, setAddQ] = useState('');
  const [addSelection, setAddSelection] = useState([]); // student_ids
  const [addSaving, setAddSaving] = useState(false);
  const [allStudents, setAllStudents] = useState([]); // { student_id, user_id, name, email, username }
  const [confirmRemove, setConfirmRemove] = useState(null); // {student_id, name}
  // Fase B-5C: mover alumno
  const [moveState, setMoveState] = useState(null); // { student_id, name, targetGroupId }
  const [moveSaving, setMoveSaving] = useState(false);

  const load = async () => {
    try {
      const [g, u] = await Promise.all([
        api.get('/groups'),
        api.get('/users'),
      ]);
      setGroups(g.data || []);
      setTeachers((u.data || []).filter(x => x.role === 'teacher'));
      // Fase B-5B: precargar conteos de alumnos por grupo
      const groups = g.data || [];
      const results = await Promise.all(groups.map(gr =>
        api.get(`/groups/${gr.id}/students`).then(r => [gr.id, r.data || []]).catch(() => [gr.id, []])
      ));
      const map = {}; results.forEach(([k,v]) => { map[k] = v; });
      setMembersByGroup(map);
      // Precargar catálogo global de alumnos (student docs + user info)
      const usersData = u.data || [];
      const studentUsers = usersData.filter(x => x.role === 'student');
      const studentDocs = await api.get('/admin/students-list').then(r => r.data || []).catch(() => []);
      const catalog = studentDocs.map(sd => {
        const uu = studentUsers.find(x => x.id === sd.user_id) || {};
        return {
          student_id: sd.id, user_id: sd.user_id,
          first_name: uu.first_name || '', last_name: uu.last_name || '',
          email: uu.email || '', username: uu.username || null,
          name: `${uu.first_name || ''} ${uu.last_name || ''}`.trim() || uu.email || 'Alumno',
        };
      });
      setAllStudents(catalog);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando grupos');
    }
  };
  useEffect(() => { load(); }, []);

  // Además de users, necesitamos el teacher_id (colección teachers) para asignar.
  // Reutilizo /admin/students-list para students? No, aquí necesito teachers.
  // Uso un endpoint indirecto: creamos un mapa user_id → teacher_id vía /admin/students-list? No, esa colección es students.
  // Solución simple: pedir /users (obtenemos user.id de teachers) y en el POST /groups enviaremos user.id como teacher_id?
  // El backend usa teacher_id de la colección teachers. Necesitamos un lookup.
  // Alternativa: en Users.jsx ya hay flujo similar; aquí uso un endpoint auxiliar.
  const [teacherMap, setTeacherMap] = useState({}); // user_id → teacher_id
  useEffect(() => {
    // Obtener teacher docs vía /teachers (si existe) o inferir a partir de /admin/students-list (no aplica).
    // Emergent: existe /teachers endpoint? Buscamos alternativa: /users trae user.id; el teacher_id se resuelve backend.
    // Solución: extendemos POST /groups para aceptar teacher_user_id?  Por ahora, usamos endpoint auxiliar directo.
    api.get('/teachers').then(r => {
      const m = {};
      (r.data || []).forEach(t => { m[t.user_id] = t.id; });
      setTeacherMap(m);
    }).catch(() => {});
  }, []);

  const teacherNameById = useMemo(() => {
    // map teacher_id (colección teachers) → "Nombre Apellido"
    const m = {};
    teachers.forEach(u => {
      const tid = teacherMap[u.id];
      if (tid) m[tid] = `${u.first_name} ${u.last_name}`.trim();
    });
    return m;
  }, [teachers, teacherMap]);

  const filtered = useMemo(() => {
    return (groups || []).filter(g => {
      if (teacherFilter !== 'all' && g.teacher_id !== teacherFilter) return false;
      if (moduleFilter !== 'all' && String(g.module_slot || '') !== moduleFilter) return false;
      if (statusFilter === 'open' && !g.active) return false;
      if (statusFilter === 'closed' && g.active) return false;
      if (q.trim()) {
        const s = q.toLowerCase();
        const hay = [g.group_code, g.name, g.description, teacherNameById[g.teacher_id]]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [groups, q, teacherFilter, moduleFilter, statusFilter, teacherNameById]);

  const openCreate = () => {
    setForm(emptyForm); setEditingId(null); setOpen(true);
  };
  const openEdit = (g) => {
    setForm({
      group_code: g.group_code || '',
      name: g.name || '',
      module_slot: g.module_slot ? String(g.module_slot) : '',
      teacher_id: g.teacher_id || '',
      schedule_days: Array.isArray(g.schedule_days) ? g.schedule_days : [],
      start_time: g.start_time || '19:00',
      end_time: g.end_time || '20:00',
      active: g.active !== false,
      description: g.description || '',
    });
    setEditingId(g.id);
    setOpen(true);
  };

  const toggleDay = (key) => {
    setForm(f => ({ ...f, schedule_days: f.schedule_days.includes(key)
      ? f.schedule_days.filter(d => d !== key)
      : [...f.schedule_days, key] }));
  };

  const submit = async () => {
    if (!form.group_code?.trim()) { toast.error('Código del grupo es obligatorio'); return; }
    if (!form.module_slot) { toast.error('Selecciona un módulo'); return; }
    if (!form.teacher_id) { toast.error('Selecciona un profesor'); return; }
    setSaving(true);
    try {
      const payload = {
        group_code: form.group_code.trim(),
        name: form.name?.trim() || form.group_code.trim(),
        module_slot: Number(form.module_slot),
        teacher_id: form.teacher_id,
        schedule_days: form.schedule_days,
        start_time: form.start_time,
        end_time: form.end_time,
        active: !!form.active,
        description: form.description || '',
      };
      if (editingId) {
        await api.patch(`/groups/${editingId}`, payload);
        toast.success('Grupo actualizado');
      } else {
        await api.post('/groups', payload);
        toast.success('Grupo creado');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto">
      <PageHeader
        title={<span className="flex items-center gap-2"><GraduationCap className="w-7 h-7 text-blue-600"/> Academia · Grupos</span>}
        subtitle="Administra los grupos, profesores, horarios y módulos de Chess4Kings."
        testId="admin-groups-header"
        right={
          <Button data-testid="new-group-btn" onClick={openCreate}
            className="rounded-full bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-1"/> Nuevo grupo
          </Button>
        }
      />

      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <div className="relative md:col-span-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input data-testid="groups-search" placeholder="Buscar…" value={q}
            onChange={(e)=>setQ(e.target.value)} className="pl-9 rounded-full"/>
        </div>
        <Select value={teacherFilter} onValueChange={setTeacherFilter}>
          <SelectTrigger data-testid="filter-teacher" className="rounded-full"><SelectValue placeholder="Profesor"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los profesores</SelectItem>
            {teachers.map(t => teacherMap[t.id] && (
              <SelectItem key={t.id} value={teacherMap[t.id]}>{t.first_name} {t.last_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={moduleFilter} onValueChange={setModuleFilter}>
          <SelectTrigger data-testid="filter-module" className="rounded-full"><SelectValue placeholder="Módulo"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los módulos</SelectItem>
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <SelectItem key={n} value={String(n)}>Módulo {n} · {moduleTier(n).label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="filter-status" className="rounded-full"><SelectValue placeholder="Estado"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="open">Abierto</SelectItem>
            <SelectItem value="closed">Cerrado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Lista */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.length === 0 && (
          <div className="md:col-span-2 rounded-2xl border border-dashed border-slate-300 p-12 text-center text-sm text-slate-500" data-testid="groups-empty">
            No hay grupos que coincidan con los filtros.
          </div>
        )}
        {filtered.map(g => {
          const tier = moduleTier(g.module_slot);
          const days = (g.schedule_days || []).map(d => DAY_ES[d]?.full).filter(Boolean).join(' y ') || '—';
          const time = (g.start_time && g.end_time) ? `${g.start_time} – ${g.end_time}` : '—';
          const teacherName = teacherNameById[g.teacher_id] || '—';
          return (
            <div key={g.id} data-testid={`group-card-${g.id}`}
              className="rounded-2xl bg-white border border-slate-200 p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">GRUPO {g.group_code || '—'}</div>
                  <div className="font-display font-extrabold text-xl truncate">{g.name || 'Sin nombre'}</div>
                </div>
                <button data-testid={`group-edit-${g.id}`} onClick={()=>openEdit(g)}
                  className="p-2 rounded-full hover:bg-slate-100 text-slate-600" title="Editar">
                  <Pencil className="w-4 h-4"/>
                </button>
              </div>
              <div className="mt-2 space-y-1 text-sm text-slate-700">
                <div className="flex items-center gap-2"><UserIcon className="w-4 h-4 text-slate-400"/>Profesor: <b className="text-slate-800">{teacherName}</b></div>
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-slate-400"/>
                  {g.module_slot ? (
                    <>Módulo {g.module_slot} · <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${tier.badge}`}>{tier.label}</span></>
                  ) : <span className="text-slate-400">Sin módulo</span>}
                </div>
                <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400"/>{days}</div>
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400"/>{time}</div>
              </div>
              <div className="mt-2">
                {g.active !== false
                  ? <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full"><CheckCircle2 className="w-3.5 h-3.5"/> Abierto</span>
                  : <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full"><Circle className="w-3.5 h-3.5"/> Cerrado</span>}
              </div>
              {/* Fase B-5B: contador + acceso a gestión de alumnos */}
              <button
                data-testid={`group-members-${g.id}`}
                onClick={()=>{ setMembersGroup(g); setMembersOpen(true); }}
                className="mt-2 w-full flex items-center justify-between px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 hover:bg-blue-50 hover:border-blue-300 transition-colors text-sm"
              >
                <span className="flex items-center gap-2 text-slate-700"><Users2 className="w-4 h-4 text-slate-500"/> Alumnos</span>
                <span className="font-mono font-bold text-blue-700">{(membersByGroup[g.id] || []).length}</span>
              </button>
            </div>
          );
        })}
      </div>

      {/* Modal crear/editar */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="group-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>{editingId ? 'Editar grupo' : 'Nuevo grupo'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Código *</Label>
                <Input data-testid="group-code" placeholder="201" value={form.group_code}
                  onChange={(e)=>setForm({...form, group_code:e.target.value})}/>
              </div>
              <div>
                <Label>Nombre</Label>
                <Input data-testid="group-name" placeholder="Principiantes" value={form.name}
                  onChange={(e)=>setForm({...form, name:e.target.value})}/>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Módulo *</Label>
                <Select value={form.module_slot} onValueChange={(v)=>setForm({...form, module_slot:v})}>
                  <SelectTrigger data-testid="group-module"><SelectValue placeholder="Selecciona"/></SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                      <SelectItem key={n} value={String(n)}>Módulo {n} · {moduleTier(n).label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Profesor *</Label>
                <Select value={form.teacher_id} onValueChange={(v)=>setForm({...form, teacher_id:v})}>
                  <SelectTrigger data-testid="group-teacher"><SelectValue placeholder="Selecciona"/></SelectTrigger>
                  <SelectContent>
                    {teachers.map(t => teacherMap[t.id] && (
                      <SelectItem key={t.id} value={teacherMap[t.id]}>{t.first_name} {t.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Días</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {DAYS.map(d => (
                  <button
                    key={d.key}
                    type="button"
                    data-testid={`day-${d.key}`}
                    onClick={()=>toggleDay(d.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      form.schedule_days.includes(d.key)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Hora inicio</Label>
                <Input data-testid="group-start" type="time" value={form.start_time}
                  onChange={(e)=>setForm({...form, start_time:e.target.value})}/>
              </div>
              <div>
                <Label>Hora fin</Label>
                <Input data-testid="group-end" type="time" value={form.end_time}
                  onChange={(e)=>setForm({...form, end_time:e.target.value})}/>
              </div>
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={form.active ? 'open' : 'closed'} onValueChange={(v)=>setForm({...form, active: v==='open'})}>
                <SelectTrigger data-testid="group-status"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Abierto</SelectItem>
                  <SelectItem value="closed">Cerrado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancelar</Button>
            <Button data-testid="group-save" onClick={submit} disabled={saving}
              className="rounded-full bg-blue-600 hover:bg-blue-700">
              {saving ? 'Guardando…' : (editingId ? 'Guardar cambios' : 'Crear grupo')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Fase B-5D: Ficha del grupo (evolución del drawer B-5B con metadatos completos + Editar) */}
      <Dialog open={membersOpen} onOpenChange={(v)=>{ if(!v){ setMembersOpen(false); setMembersGroup(null);} }}>
        <DialogContent data-testid="group-detail-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-blue-600"/>
              {membersGroup ? `Grupo ${membersGroup.group_code || membersGroup.name || ''}` : 'Grupo'}
            </DialogTitle>
          </DialogHeader>
          {membersGroup && (() => {
            const mem = membersByGroup[membersGroup.id] || [];
            const tier = moduleTier(membersGroup.module_slot);
            const days = (membersGroup.schedule_days || []).map(d => DAY_ES[d]?.full).filter(Boolean).join(' y ') || '—';
            const time = (membersGroup.start_time && membersGroup.end_time) ? `${membersGroup.start_time} – ${membersGroup.end_time}` : '—';
            const teacherName = teacherNameById[membersGroup.teacher_id] || '—';
            return (
              <div className="space-y-4">
                {/* Metadatos del grupo */}
                <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold" data-testid="detail-code">GRUPO {membersGroup.group_code || '—'}</div>
                      <div className="font-display font-extrabold text-lg truncate" data-testid="detail-name">{membersGroup.name || 'Sin nombre'}</div>
                      {membersGroup.module_slot && tier && (
                        <div className="mt-1 text-sm text-slate-700" data-testid="detail-module">
                          Módulo {membersGroup.module_slot} · <span className={`text-[11px] px-2 py-0.5 rounded-full font-bold ${tier.badge}`}>{tier.label}</span>
                        </div>
                      )}
                    </div>
                    <Button data-testid="detail-edit-btn" size="sm" variant="outline"
                      onClick={()=>{ setMembersOpen(false); openEdit(membersGroup); }}
                      className="rounded-full">
                      <Pencil className="w-4 h-4 mr-1"/> Editar grupo
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
                    <div className="flex items-center gap-2" data-testid="detail-teacher"><UserIcon className="w-4 h-4 text-slate-400"/>Profesor: <b className="text-slate-800">{teacherName}</b></div>
                    <div className="flex items-center gap-2" data-testid="detail-days"><Calendar className="w-4 h-4 text-slate-400"/>{days}</div>
                    <div className="flex items-center gap-2" data-testid="detail-time"><Clock className="w-4 h-4 text-slate-400"/>{time}</div>
                    <div className="flex items-center gap-2" data-testid="detail-status">
                      {membersGroup.active !== false
                        ? <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full"><CheckCircle2 className="w-3.5 h-3.5"/> Grupo abierto</span>
                        : <span className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full"><Circle className="w-3.5 h-3.5"/> Grupo cerrado</span>}
                    </div>
                  </div>
                </div>

                {/* Sección Alumnos (B-5B + B-5C intactos) */}
                <div className="flex items-center justify-between">
                  <div className="text-sm text-slate-500">Alumnos · <b className="text-slate-800" data-testid="members-count">{mem.length}</b></div>
                  <Button data-testid="add-members-btn" size="sm" onClick={()=>{ setAddSelection([]); setAddQ(''); setAddOpen(true); }}
                    className="rounded-full bg-blue-600 hover:bg-blue-700">
                    <UserPlus className="w-4 h-4 mr-1"/> Agregar alumnos
                  </Button>
                </div>
                <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 divide-y">
                  {mem.length === 0 && (
                    <div className="p-6 text-center text-sm text-slate-500" data-testid="members-empty">
                      Este grupo aún no tiene alumnos.
                    </div>
                  )}
                  {mem.map(s => (
                    <div key={s.student_id} data-testid={`member-row-${s.student_id}`}
                      className="flex items-center justify-between px-3 py-2.5 hover:bg-slate-50">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate">{s.first_name} {s.last_name}</div>
                        <div className="text-xs text-slate-500 truncate">{s.username || s.email}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`member-move-${s.student_id}`}
                          onClick={()=>setMoveState({ student_id: s.student_id, name: `${s.first_name} ${s.last_name}`.trim(), targetGroupId: '' })}
                          className="text-blue-700 hover:bg-blue-50 rounded-full p-2"
                          title="Mover a otro grupo"
                        >
                          <ArrowRightLeft className="w-4 h-4"/>
                        </button>
                        <button
                          data-testid={`member-remove-${s.student_id}`}
                          onClick={()=>setConfirmRemove({ student_id: s.student_id, name: `${s.first_name} ${s.last_name}`.trim() })}
                          className="text-rose-600 hover:bg-rose-50 rounded-full p-2"
                          title="Quitar del grupo"
                        >
                          <UserMinus className="w-4 h-4"/>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button data-testid="detail-back" variant="outline" onClick={()=>{ setMembersOpen(false); setMembersGroup(null); }}>
              ← Volver a grupos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fase B-5B: modal para agregar alumnos */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="add-members-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>Agregar alumnos al grupo</DialogTitle></DialogHeader>
          {membersGroup && (() => {
            const memIds = new Set((membersByGroup[membersGroup.id] || []).map(s => s.student_id));
            const filteredCat = allStudents.filter(s => {
              if (!addQ.trim()) return true;
              const hay = `${s.name} ${s.email} ${s.username || ''}`.toLowerCase();
              return hay.includes(addQ.toLowerCase());
            });
            const toggle = (sid) => {
              setAddSelection(sel => sel.includes(sid) ? sel.filter(x => x !== sid) : [...sel, sid]);
            };
            return (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                  <Input data-testid="add-members-search" placeholder="Buscar alumno…"
                    value={addQ} onChange={(e)=>setAddQ(e.target.value)} className="pl-9 rounded-full"/>
                </div>
                <div className="text-xs text-slate-500">
                  Seleccionados: <b data-testid="add-selection-count">{addSelection.length}</b>
                </div>
                <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 divide-y">
                  {filteredCat.length === 0 && <div className="p-6 text-center text-sm text-slate-500">Sin resultados.</div>}
                  {filteredCat.map(s => {
                    const isMember = memIds.has(s.student_id);
                    const isSel = addSelection.includes(s.student_id);
                    return (
                      <label key={s.student_id} data-testid={`add-row-${s.student_id}`}
                        className={`flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer ${isMember ? 'bg-emerald-50/50 cursor-not-allowed' : 'hover:bg-slate-50'}`}>
                        <input type="checkbox" disabled={isMember} checked={isSel} onChange={()=>toggle(s.student_id)}
                          className="w-4 h-4 accent-blue-600" data-testid={`add-check-${s.student_id}`}/>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold truncate">{s.name}</div>
                          <div className="text-xs text-slate-500 truncate">{s.username || s.email}</div>
                        </div>
                        {isMember && <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Ya en grupo</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setAddOpen(false)}>Cancelar</Button>
            <Button
              data-testid="add-members-submit"
              disabled={addSaving || addSelection.length === 0}
              onClick={async () => {
                if (!membersGroup) return;
                setAddSaving(true);
                try {
                  await Promise.all(addSelection.map(sid =>
                    api.post(`/groups/${membersGroup.id}/students`, { student_id: sid })
                  ));
                  toast.success('Alumnos agregados');
                  setAddOpen(false);
                  setAddSelection([]);
                  await load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Error al agregar');
                } finally { setAddSaving(false); }
              }}
              className="rounded-full bg-blue-600 hover:bg-blue-700"
            >
              {addSaving ? 'Agregando…' : `Agregar ${addSelection.length || ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fase B-5B: confirmación para quitar */}
      <Dialog open={!!confirmRemove} onOpenChange={(v)=>{ if(!v) setConfirmRemove(null); }}>
        <DialogContent data-testid="confirm-remove-dialog" className="max-w-md">
          <DialogHeader><DialogTitle>Quitar alumno del grupo</DialogTitle></DialogHeader>
          {confirmRemove && membersGroup && (
            <div className="text-sm text-slate-700">
              ¿Quitar a <b>{confirmRemove.name}</b> del <b>Grupo {membersGroup.group_code || membersGroup.name}</b>?
              <div className="mt-2 text-xs text-slate-500">
                Esto solo elimina la relación con este grupo. El alumno seguirá existiendo en la plataforma y sus otras membresías no se verán afectadas.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setConfirmRemove(null)}>Cancelar</Button>
            <Button
              data-testid="confirm-remove-submit"
              className="rounded-full bg-rose-600 hover:bg-rose-700"
              onClick={async () => {
                if (!membersGroup || !confirmRemove) return;
                try {
                  await api.delete(`/groups/${membersGroup.id}/students/${confirmRemove.student_id}`);
                  toast.success('Alumno retirado del grupo');
                  setConfirmRemove(null);
                  await load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Error al quitar');
                }
              }}
            >
              Quitar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Fase B-5C: modal mover alumno */}
      <Dialog open={!!moveState} onOpenChange={(v)=>{ if(!v) setMoveState(null); }}>
        <DialogContent data-testid="move-dialog" className="max-w-md">
          <DialogHeader><DialogTitle>Mover alumno a otro grupo</DialogTitle></DialogHeader>
          {moveState && membersGroup && (() => {
            // Grupos destino válidos: activos, distintos del origen
            const destinos = groups.filter(g => g.id !== membersGroup.id && g.active !== false);
            const dest = destinos.find(g => g.id === moveState.targetGroupId);
            return (
              <div className="space-y-3 text-sm">
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <div className="text-xs text-slate-500 uppercase tracking-widest font-bold">Alumno</div>
                  <div className="font-semibold" data-testid="move-student-name">{moveState.name}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-widest font-bold mt-2">Grupo actual</div>
                  <div className="font-mono" data-testid="move-source-group">Grupo {membersGroup.group_code || membersGroup.name}</div>
                </div>
                <div>
                  <label className="text-xs text-slate-500 uppercase tracking-widest font-bold">Nuevo grupo *</label>
                  <Select value={moveState.targetGroupId} onValueChange={(v)=>setMoveState({...moveState, targetGroupId: v})}>
                    <SelectTrigger data-testid="move-target-select"><SelectValue placeholder="Selecciona un grupo destino…"/></SelectTrigger>
                    <SelectContent>
                      {destinos.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No hay grupos destino disponibles.</div>}
                      {destinos.map(g => (
                        <SelectItem key={g.id} value={g.id}>
                          Grupo {g.group_code || '—'} · {g.name || 'Sin nombre'}
                          {g.module_slot ? ` · Módulo ${g.module_slot}` : ''}
                          {teacherNameById[g.teacher_id] ? ` · ${teacherNameById[g.teacher_id]}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {dest && (
                  <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
                    El alumno <b>dejará de pertenecer a Grupo {membersGroup.group_code}</b> y será asignado a <b>Grupo {dest.group_code || dest.name}</b>. Sus demás membresías (si las hay) no se verán afectadas.
                  </div>
                )}
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setMoveState(null)}>Cancelar</Button>
            <Button
              data-testid="move-submit"
              disabled={moveSaving || !moveState?.targetGroupId}
              className="rounded-full bg-blue-600 hover:bg-blue-700"
              onClick={async () => {
                if (!moveState || !membersGroup) return;
                setMoveSaving(true);
                try {
                  const to = moveState.targetGroupId;
                  const destName = groups.find(g => g.id === to);
                  await api.post(`/admin/students/${moveState.student_id}/move-to-group`, {
                    to_group_id: to,
                    from_group_id: membersGroup.id,
                  });
                  toast.success(`${moveState.name} fue movido correctamente a Grupo ${destName?.group_code || destName?.name || ''}`);
                  setMoveState(null);
                  await load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Error al mover');
                } finally { setMoveSaving(false); }
              }}
            >
              {moveSaving ? 'Moviendo…' : 'Mover alumno'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
